import { ClientBenchmarkingModel, ClientMarketExpansionModel, ClientRoleModel } from "@app/client";
import { Client, ClientCreatePayload, ClientListItem, ClientToApi, NewClientDefaults } from "@app/client/client";
import { ClientLoginStatistics } from "@app/client/clientLoginStatistics";
import { DatabaseConnectionModel } from "@app/client/databaseConnectionModel";
import ServiceBase from "@app/shared/serviceBase";
import { DashboardModel } from "@app/user/dashboardModel";
import RoleModel from "@app/user/roleModel";

class ClientService extends ServiceBase {

  private authenticationApiEndpoint = `${this.authenticationEndpoint}/api`;

  public async getAllClients(): Promise<Client[]> {
    return await this.makeGet<Client[]>("clients");
  }
  public async getActiveClientsList(signal?: AbortSignal): Promise<ClientListItem[]> {
    return await this.makeGet<ClientListItem[]>("clients/client-list", null, undefined, false, undefined, signal);
  }
  public async getRegulatorsList(): Promise<ClientListItem[]> {
    return await this.makeGet<ClientListItem[]>("clients/regulators-list");
  }
  public async getClient(id: number): Promise<Client> {
    return await this.makeGet<Client>(`clients/${id}`);
  }
  public async getNewClientDefaults(): Promise<NewClientDefaults> {
    return await this.makeGet<NewClientDefaults>(`clients/new-client-defaults`);
  }
  public async createClient(clientModel: ClientCreatePayload): Promise<Client> {
    return await this.makePost<Client>("clients/", clientModel);
  }
  public async updateClient(clientModel: ClientToApi): Promise<void> {
    return await this.makePut(`clients/${clientModel.clientId}`, clientModel);
  }
  public async deactivateClient(id: number): Promise<void> {
    return await this.makePatch<void>(`clients/${id}/deactivate`);
  }
  public async activateClient(id: number): Promise<void> {
    return await this.makePatch<void>(`clients/${id}/activate`);
  }
  public async getAllDatabaseConnections(): Promise<DatabaseConnectionModel[]> {
    return await this.makeGet<DatabaseConnectionModel[]>("clients/connections");
  }
  /**
   * Calls the api account controller in Omnius.Authentication to create guest logins for the specified client id.
   * This is a long-running API call because all the work to create guest logins
   * has to be done during this call. Timeout for this call is set to 20 minutes.
   * @param id
   */
  public async createGuestLogins(id: number): Promise<void> {
    await this.makePatch<void>(`/account/add-guest-logins-by-client/${id}`, null, false, this.authenticationApiEndpoint, 20 * 60);
  }
  /**
   * Gets all roles that are available for assignment to the given client, filtering out certain roles
   * that shouldn't be visible to non-Quest clients.
   * Intended for use in seeding the Roles list when creating a new client.
   * @param clientId
   */
  /**
   * Gets all available roles, filtering out certain roles that shouldn't be visible to non-Quest clients.
   * Intended for use in seeding the Roles list in the Client Admin UI.
   * @param clientId
   */
  public async getAvailableRoles(clientId?: number): Promise<RoleModel[]> {
    return await this.makeGet<RoleModel[]>(`clients/available-roles${clientId ? `?ClientId=${clientId}` : ""}`);
  }
  /**
   * Gets all available roles, filtering out certain roles that shouldn't be visible to non-Quest clients.
   * Intended for use in seeding the Roles list in the Client Admin UI.
   * @param clientId
   */
  public async getAvailableDefaultRoles(): Promise<RoleModel[]> {
    return await this.makeGet<RoleModel[]>(`clients/available-default-roles`);
  }
  /**
   * Gets all roles identified as defaults for new clients.
   * Intended for use in seeding the Roles list in the Client Admin UI.
   */
  public async getDefaultRoles(): Promise<RoleModel[]> {
    return await this.makeGet<RoleModel[]>(`clients/default-roles`);
  }

  public async updateDefaultClientRoles(newDefaultRoles: RoleModel[]): Promise<void> {
    await this.makePatch<void>(`/clients/default-roles`, newDefaultRoles);
  }

  /**
   * Gets all dashboards currently defined in the sytem. Intended for use in Client Admin UI
   * */
  public async getAllDashboards(): Promise<DashboardModel[]> {
    return await this.makeGet<DashboardModel[]>("clients/dashboards");
  }
  /**
   * Gets all roles currently assigned to the specified client, filtering out certain roles that shouldn't be
   * visible to non-Quest clients and Quest users who don't already hold certain roles.
   * @param clientId
   */
  public async getClientRoles(clientId: number): Promise<ClientRoleModel[]> {
    return await this.makeGet(`clients/${clientId}/assigned-roles`);
  }
  /**
   * Gets all domains currently assigned to the specified client.
   * @param clientId
   */
  public async getClientDomains(clientId: number): Promise<Array<{ domain: string }>> {
    return await this.makeGet(`clients/${clientId}/domains`);
  }

  public async getLoginStatisticsByClient(clientId: number, includeGuestLogins: boolean): Promise<ClientLoginStatistics> {
    return await this.makeGet(`/clients/${clientId}/user-login-stats?includeGuestLogins=${includeGuestLogins}`);
  }

  public async getClientBenchmarkingData(clientId: number): Promise<ClientBenchmarkingModel[]> {
    if (!clientId) {
      return [];
    }

    return await this.makeGet<ClientBenchmarkingModel[]>(`clients/${clientId}/client-benchmarking`);
  }

  public async updateClientBenchmarkingData(
    clientId: number,
    clientBenchmarkingModels: ClientBenchmarkingModel[]
  ): Promise<boolean> {
    if (!clientId) {
      return false;
    }

    return await this.makePost<boolean>(`clients/${clientId}/client-benchmarking`, { clientBenchmarkingModels });
  }

  public async getClientMarketExpansionData(clientId: number): Promise<ClientMarketExpansionModel[]> {
    if (!clientId) {
      return [];
    }

    return await this.makeGet<ClientMarketExpansionModel[]>(`clients/${clientId}/client-market-expansion`);
  }

  public async updateClientMarketExpansionData(
    clientId: number,
    clientMarketExpansionModels: ClientMarketExpansionModel[]
  ): Promise<boolean> {
    if (!clientId) {
      return false;
    }

    return await this.makePost<boolean>(`clients/${clientId}/client-market-expansion`, { clientMarketExpansionModels });
  }
}

export {
  ClientService,
};
